import { CargoApiRepository } from "@/app/infrastructures/repositories/api/CargoApiRepository";
import {
  EditCargoRequestInteface,
  AppendBagOrSttNumberInterface,
  AddCargoRequestInteface,
  UpdateRetryCargoRequestInterface,
  SearchFlightRequestInterface,
  CheckAvailableRequestInterface,
  AwbValidationRequestInterface
} from "@/data/payload/contracts/CargoRequest";
import {
  AirportDetail,
  AirportEntities,
  AirportRouteTypeData,
  CalculateVolume,
  CargoAddResponse,
  CargoCommodityList,
  CargoEditResponse,
  CargoEntities,
  CargoProductEntities,
  FlightAvailable,
  ListLastReadCargo,
  SearchCargoOptions,
  SearchFlight,
  CargoDisputeSummaryData,
  CargoMinimumWeightSummaryData,
  CargoSummaryInProcessData,
  CargoSuccessSummaryData,
  CargoFailedUnread,
  CheckEligibleReadyToCargo
} from "@/domain/entities/Cargo";
import { injectable } from "tsyringe";
import {
  ResponseBagSttDetail,
  ResponsePayload
} from "@/domain/entities/ResponsePayload";
import {
  CalculateVolumeApiRequest,
  CargoListRequest,
  CargoMinimumWeightSummaryApiRequest,
  CargoSummaryInProcessRequest,
  DownloadCargoListApiRequest,
  FailedUnreadRequest,
  AirportRouteTypeApiRequest
} from "@/data/payload/api/CargoApiRequest";
import { AwbDetailData } from "@/domain/entities/NGen";
import { RequestCheckEligibleReadyToCargo } from "@/domain/entities/ReadyToCargo";

@injectable()
export class CargoPresenter {
  private repository: CargoApiRepository;

  constructor(repo: CargoApiRepository) {
    this.repository = repo;
  }

  public getCargoList(params: CargoListRequest): Promise<CargoEntities> {
    return this.repository.getCargoList(params);
  }
  public getRetryCargoList(params: CargoListRequest): Promise<CargoEntities> {
    return this.repository.getRetryCargoList(params);
  }
  public getDetailCargo(id: string | number) {
    return this.repository.getDetailCargo(id);
  }

  public getDetailCargoByCargoNo(cargoNo: string) {
    return this.repository.getDetailCargoByCargoNo(cargoNo);
  }
  public getDetailRetryCargo(rcNo: string, version?: string) {
    return this.repository.getDetailRetryCargo(rcNo, version);
  }
  public editCargo(
    payload: EditCargoRequestInteface
  ): Promise<CargoEditResponse> {
    return this.repository.editCargo(payload);
  }

  public getCargoProductList(status: string): Promise<CargoProductEntities> {
    return this.repository.getCargoProductList(status);
  }

  public getCargoCommodityList(
    page: number,
    limit: number,
    search: string
  ): Promise<CargoCommodityList> {
    return this.repository.getCargoCommodityList(page, limit, search);
  }

  public appendBagOrSttNumber(
    payload: AppendBagOrSttNumberInterface
  ): Promise<ResponseBagSttDetail> {
    return this.repository.appendBagOrSttNumber(payload);
  }

  public getSearchCargoOptions(
    origin: string,
    destination: string
  ): Promise<SearchCargoOptions> {
    return this.repository.getSearchCargoOptions(origin, destination);
  }

  public getAirportList(
    airportCode: string,
    cityCode: string,
    page: number,
    limit: number,
    isLocation?: boolean,
    search?: string
  ): Promise<AirportEntities> {
    return this.repository.getAirportList(
      airportCode,
      cityCode,
      page,
      limit,
      isLocation,
      search
    );
  }

  public getAirportDetail(airportCode: string): Promise<AirportDetail> {
    return this.repository.getAirportDetail(airportCode);
  }

  public getAirportRouteType(
    payload: AirportRouteTypeApiRequest
  ): Promise<AirportRouteTypeData> {
    return this.repository.getAirportRouteType(payload);
  }

  public createCargo(
    payload: AddCargoRequestInteface
  ): Promise<CargoAddResponse> {
    return this.repository.createCargo(payload);
  }

  public updateRetryCargo(
    id: number,
    payload: UpdateRetryCargoRequestInterface,
    version = "v1"
  ): Promise<ResponsePayload> {
    return this.repository.updateRetryCargo(id, payload, version);
  }

  public getDownloadData(id: number, type: string, version: string): Promise<string> {
    return this.repository.getDownloadReport(id, type, version);
  }
  public getListLastCargo(): Promise<ListLastReadCargo[]> {
    return this.repository.getLastReadCargo();
  }
  public updateListLastCargo(): Promise<boolean> {
    return this.repository.updateLastReadCargo();
  }
  public getLastIdRetryCargo(params: CargoListRequest): Promise<number> {
    return this.repository.getLastIdRetryCargo(params);
  }
  public getLastIdCargo(params: CargoListRequest): Promise<number> {
    return this.repository.getLastIdCargo(params);
  }
  public searchFlight(payload: SearchFlightRequestInterface): Promise<SearchFlight[]> {
    return this.repository.searchFlight(payload);
  }
  public checkAvailable(
    payload: CheckAvailableRequestInterface
  ): Promise<FlightAvailable> {
    return this.repository.checkAvailable(payload);
  }

  public getCargoDisputeSummary(params: CargoListRequest): Promise<CargoDisputeSummaryData> {
    return this.repository.getCargoDisputeSummary(params);
  }

    public getCargoSuccessSummary(params: CargoListRequest): Promise<CargoSuccessSummaryData> {
    return this.repository.getCargoSuccessSummary(params);
  }

  public downloadExcelCargoDisputeSummary(payload: any): Promise<CargoDisputeSummaryData> {
    return this.repository.downlaodExcelCargoDisputeSummary(payload);
  }

  getAwbValidation(
    payload: AwbValidationRequestInterface
  ): Promise<AwbDetailData> {
    return this.repository.getAwbValidation(payload);
  }

  calculateVolume(
    payload: CalculateVolumeApiRequest
  ): Promise<CalculateVolume> {
    return this.repository.calculateVolume(payload);
  }

  public getCargoMinimumWeightSummary(
    params: CargoMinimumWeightSummaryApiRequest
  ): Promise<CargoMinimumWeightSummaryData> {
    return this.repository.getCargoMinimumWeightSummary(params);
  }

  public downloadMinimumVolume(params: any): Promise<any> {
    return this.repository.downloadMinimumVolume(params);
  }

  public getSummaryInProcess(params: CargoSummaryInProcessRequest): Promise<CargoSummaryInProcessData> {
    return this.repository.getSummaryInProcess(params)
  }

  public downloadListCargoSuccess(
    payload: DownloadCargoListApiRequest
  ): Promise<ResponsePayload> {
    return this.repository.downloadListCargoSuccess(payload);
  }

  public getCargoFailedUnread(params: FailedUnreadRequest): Promise<CargoFailedUnread> {
    return this.repository.getCargoFailedUnread(params);
  }

  public checkEligible(params: RequestCheckEligibleReadyToCargo): Promise<CheckEligibleReadyToCargo> {
    return this.repository.checkEligible(params);
  }
}
